import { useCallback, useRef, useState } from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import UnderlineExt from '@tiptap/extension-underline'
import ImageExt from '@tiptap/extension-image'
import LinkExt from '@tiptap/extension-link'
import TextAlign from '@tiptap/extension-text-align'
import Placeholder from '@tiptap/extension-placeholder'
import { TextStyle, Color } from '@tiptap/extension-text-style'
import Highlight from '@tiptap/extension-highlight'
import { Table as TableExt } from '@tiptap/extension-table'
import TableRow from '@tiptap/extension-table-row'
import TableHeader from '@tiptap/extension-table-header'
import TableCell from '@tiptap/extension-table-cell'
import { api } from '../utils/api'
import {
  Bold, Italic, Underline, Strikethrough,
  Heading1, Heading2, Heading3,
  List, ListOrdered, Quote, Code, Code2,
  AlignLeft, AlignCenter, AlignRight,
  Link2, Unlink, Image, Minus,
  Undo2, Redo2, Highlighter, Upload,
  Table as TableIcon, Columns, Rows, Trash2,
} from 'lucide-react'

interface Props {
  content: string
  onChange: (html: string) => void
  placeholder?: string
  uploadEndpoint?: string
}

function ToolBtn({ active = false, disabled = false, onClick, title, children }: {
  active?: boolean; disabled?: boolean
  onClick: () => void; title: string; children: React.ReactNode
}) {
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onClick={onClick}
      className={`p-1.5 rounded transition-colors ${
        active
          ? 'bg-blue-600 text-white'
          : disabled
            ? 'text-gray-300 cursor-not-allowed'
            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
      }`}
    >
      {children}
    </button>
  )
}

function Sep() {
  return <div className="w-px h-5 bg-gray-200 mx-0.5 self-center"/>
}

export default function RichTextEditor({ content, onChange, placeholder = 'เขียนเนื้อหา...', uploadEndpoint = '/knowledge/upload-image' }: Props) {
  const fileRef = useRef<HTMLInputElement>(null)
  const [linkDialog, setLinkDialog] = useState(false)
  const [linkUrl, setLinkUrl] = useState('')
  const [uploading, setUploading] = useState(false)
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null)

  const uploadImage = useCallback(async (file: File): Promise<string> => {
    const fd = new FormData()
    fd.append('image', file)
    const r = await api.post(uploadEndpoint, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return r.data.url
  }, [uploadEndpoint])

  const editor = useEditor({
    extensions: [
      StarterKit,
      UnderlineExt,
      ImageExt.configure({ inline: false }),
      LinkExt.configure({ openOnClick: false, HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' } }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Placeholder.configure({ placeholder }),
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      TableExt.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content,
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: {
      handlePaste(_, event) {
        const items = event.clipboardData?.items
        if (!items) return false
        for (const item of Array.from(items)) {
          if (item.type.startsWith('image/')) {
            const file = item.getAsFile()
            if (!file) continue
            setUploading(true)
            uploadImage(file)
              .then(url => editor?.chain().focus().setImage({ src: url }).run())
              .catch(() => alert('อัปโหลดรูปไม่สำเร็จ'))
              .finally(() => setUploading(false))
            return true
          }
        }
        return false
      },
      handleDrop(_, event) {
        const files = event.dataTransfer?.files
        if (!files?.length) return false
        for (const file of Array.from(files)) {
          if (file.type.startsWith('image/')) {
            setUploading(true)
            uploadImage(file)
              .then(url => editor?.chain().focus().setImage({ src: url }).run())
              .catch(() => alert('อัปโหลดรูปไม่สำเร็จ'))
              .finally(() => setUploading(false))
            return true
          }
        }
        return false
      },
    },
  })

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    uploadImage(file)
      .then(url => editor?.chain().focus().setImage({ src: url }).run())
      .catch(() => alert('อัปโหลดรูปไม่สำเร็จ'))
      .finally(() => { setUploading(false); if (fileRef.current) fileRef.current.value = '' })
  }

  const openLink = () => {
    const prev = editor?.getAttributes('link').href || ''
    setLinkUrl(prev)
    setLinkDialog(true)
  }
  const applyLink = () => {
    if (!linkUrl.trim()) {
      editor?.chain().focus().unsetLink().run()
    } else {
      editor?.chain().focus().setLink({ href: linkUrl.trim() }).run()
    }
    setLinkDialog(false)
    setLinkUrl('')
  }

  if (!editor) return null

  return (
    <div className="border border-gray-300 rounded-xl overflow-hidden">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-0.5 p-2 bg-gray-50 border-b border-gray-200">
        {/* Undo / Redo */}
        <ToolBtn title="Undo" onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()}>
          <Undo2 size={15}/>
        </ToolBtn>
        <ToolBtn title="Redo" onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()}>
          <Redo2 size={15}/>
        </ToolBtn>

        <Sep/>

        {/* Inline formatting */}
        <ToolBtn title="Bold (Ctrl+B)" active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()}>
          <Bold size={15}/>
        </ToolBtn>
        <ToolBtn title="Italic (Ctrl+I)" active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()}>
          <Italic size={15}/>
        </ToolBtn>
        <ToolBtn title="Underline (Ctrl+U)" active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()}>
          <Underline size={15}/>
        </ToolBtn>
        <ToolBtn title="Strikethrough" active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()}>
          <Strikethrough size={15}/>
        </ToolBtn>
        <ToolBtn title="Inline code" active={editor.isActive('code')} onClick={() => editor.chain().focus().toggleCode().run()}>
          <Code size={15}/>
        </ToolBtn>
        <ToolBtn title="Highlight" active={editor.isActive('highlight')} onClick={() => editor.chain().focus().toggleHighlight({ color: '#fef08a' }).run()}>
          <Highlighter size={15}/>
        </ToolBtn>

        <Sep/>

        {/* Headings */}
        <ToolBtn title="Heading 1" active={editor.isActive('heading', { level: 1 })} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}>
          <Heading1 size={15}/>
        </ToolBtn>
        <ToolBtn title="Heading 2" active={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
          <Heading2 size={15}/>
        </ToolBtn>
        <ToolBtn title="Heading 3" active={editor.isActive('heading', { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>
          <Heading3 size={15}/>
        </ToolBtn>

        <Sep/>

        {/* Lists */}
        <ToolBtn title="Bullet list" active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()}>
          <List size={15}/>
        </ToolBtn>
        <ToolBtn title="Numbered list" active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()}>
          <ListOrdered size={15}/>
        </ToolBtn>

        <Sep/>

        {/* Blocks */}
        <ToolBtn title="Blockquote" active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()}>
          <Quote size={15}/>
        </ToolBtn>
        <ToolBtn title="Code block" active={editor.isActive('codeBlock')} onClick={() => editor.chain().focus().toggleCodeBlock().run()}>
          <Code2 size={15}/>
        </ToolBtn>
        <ToolBtn title="Horizontal rule" onClick={() => editor.chain().focus().setHorizontalRule().run()}>
          <Minus size={15}/>
        </ToolBtn>

        <Sep/>

        {/* Alignment */}
        <ToolBtn title="Align left" active={editor.isActive({ textAlign: 'left' })} onClick={() => editor.chain().focus().setTextAlign('left').run()}>
          <AlignLeft size={15}/>
        </ToolBtn>
        <ToolBtn title="Align center" active={editor.isActive({ textAlign: 'center' })} onClick={() => editor.chain().focus().setTextAlign('center').run()}>
          <AlignCenter size={15}/>
        </ToolBtn>
        <ToolBtn title="Align right" active={editor.isActive({ textAlign: 'right' })} onClick={() => editor.chain().focus().setTextAlign('right').run()}>
          <AlignRight size={15}/>
        </ToolBtn>

        <Sep/>

        {/* Link */}
        <ToolBtn title="Insert link" active={editor.isActive('link')} onClick={openLink}>
          <Link2 size={15}/>
        </ToolBtn>
        {editor.isActive('link') && (
          <ToolBtn title="Remove link" onClick={() => editor.chain().focus().unsetLink().run()}>
            <Unlink size={15}/>
          </ToolBtn>
        )}

        <Sep/>

        {/* Image */}
        <ToolBtn title="Insert image" onClick={() => fileRef.current?.click()} disabled={uploading}>
          {uploading ? <span className="text-xs text-blue-500 animate-pulse px-1">กำลังอัปโหลด...</span> : <Image size={15}/>}
        </ToolBtn>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFileSelect}/>

        <Sep/>

        {/* Table */}
        <ToolBtn title="Insert table" active={editor.isActive('table')}
          onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}>
          <TableIcon size={15}/>
        </ToolBtn>
        {editor.isActive('table') && (
          <>
            <ToolBtn title="Add column" onClick={() => editor.chain().focus().addColumnAfter().run()}>
              <Columns size={15}/>
            </ToolBtn>
            <ToolBtn title="Add row" onClick={() => editor.chain().focus().addRowAfter().run()}>
              <Rows size={15}/>
            </ToolBtn>
            <ToolBtn title="Delete table" onClick={() => editor.chain().focus().deleteTable().run()}>
              <Trash2 size={15}/>
            </ToolBtn>
          </>
        )}

        <span className="text-xs text-gray-400 ml-1 hidden sm:inline">หรือ Ctrl+V วางรูปได้เลย</span>
      </div>

      {/* Link dialog */}
      {linkDialog && (
        <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border-b border-blue-200">
          <Link2 size={14} className="text-blue-500 flex-shrink-0"/>
          <input
            autoFocus
            value={linkUrl}
            onChange={e => setLinkUrl(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') applyLink(); if (e.key === 'Escape') setLinkDialog(false) }}
            placeholder="https://..."
            className="flex-1 text-sm bg-white border border-blue-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          <button onClick={applyLink} className="text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700">ตกลง</button>
          <button onClick={() => setLinkDialog(false)} className="text-xs text-gray-500 hover:text-gray-700">ยกเลิก</button>
        </div>
      )}

      {/* Editor content */}
      <div className="tiptap-editor bg-white" onClick={() => editor.chain().focus().run()}>
        <EditorContent editor={editor}/>
      </div>

      {/* Drop zone hint */}
      <div className="px-3 py-1.5 bg-gray-50 border-t border-gray-100 flex items-center gap-1.5 text-xs text-gray-400">
        <Upload size={11}/>
        <span>ลากวางรูปภาพมาที่นี่ หรือ Ctrl+V วางได้เลย</span>
      </div>

      {/* Lightbox for image preview */}
      {lightboxSrc && (
        <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4" onClick={() => setLightboxSrc(null)}>
          <img src={lightboxSrc} className="max-w-full max-h-full rounded-lg object-contain" alt="preview"/>
        </div>
      )}
    </div>
  )
}
